import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from '@/context/AuthContext';
import { useSync } from '@/context/SyncContext';
import { useToast } from '@/context/ToastContext';
import { indexedDBService } from '@/services/database';
import { Button, Input } from '@/components/common';
import { PhoneInput, GiftSelector } from '@/components/forms';
import { SignatureModal } from '@/components/signature';
import { ReferidoPhaseFlow } from '@/components/referidos';
import { buildReferidoMessage, VALIDATION_RULES, GIFT_MAP } from '@/constants';
import type { Referidor, Referido, ReferidoFormData, GiftCategory } from '@/types';

type CaptureStep = 'referidor' | 'message_preview' | 'referidos' | 'signature' | 'complete';

interface ReferidorFormState {
  nombre_completo: string;
  telefono: string;
  direccion: string;
  regalo_categoria: GiftCategory | null;
  regalo_detalle: string;
  monto_venta: string;
}

const initialReferidorState: ReferidorFormState = {
  nombre_completo: '',
  telefono: '',
  direccion: '',
  regalo_categoria: null,
  regalo_detalle: '',
  monto_venta: '',
};

export function CapturePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToQueue } = useSync();
  const { showToast } = useToast();

  const [step, setStep] = useState<CaptureStep>('referidor');
  const [referidorForm, setReferidorForm] = useState<ReferidorFormState>(initialReferidorState);
  const [referidos, setReferidos] = useState<ReferidoFormData[]>([]);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [messageCopied, setMessageCopied] = useState(false);

  // Mensaje dinámico para el referidor: usa el regalo que marcó y el nombre
  // de quien está capturando (asesor logueado).
  const referidoMessage = buildReferidoMessage({
    regalo: referidorForm.regalo_categoria ? GIFT_MAP[referidorForm.regalo_categoria] : undefined,
    asesor: user?.name,
  });

  // Validate referidor form
  const validateReferidor = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};
    const rules = VALIDATION_RULES.referidor;

    if (
      !referidorForm.nombre_completo ||
      referidorForm.nombre_completo.length < rules.nombre_completo.minLength
    ) {
      newErrors.nombre_completo = `Mínimo ${rules.nombre_completo.minLength} caracteres`;
    }

    if (!referidorForm.telefono || referidorForm.telefono.length !== rules.telefono.length) {
      newErrors.telefono = 'Ingresa 10 dígitos';
    }

    // "Regalo entregado" (los 6) y "Premio si compran" ahora son OPCIONALES.
    // Solo validamos el largo del texto si escribieron algo.
    if (referidorForm.regalo_detalle && referidorForm.regalo_detalle.length > 100) {
      newErrors.regalo_detalle = 'Máximo 100 caracteres';
    }

    // Monto de venta: OPCIONAL. Solo validamos que sea un número válido si lo escribieron.
    if (referidorForm.monto_venta) {
      const m = parseFloat(referidorForm.monto_venta);
      if (isNaN(m) || m <= 0) {
        newErrors.monto_venta = 'Ingresa un monto válido (ej: 1899)';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [referidorForm]);

  // Handle referidor submission → show message preview
  const handleReferidorSubmit = () => {
    if (validateReferidor()) {
      setStep('message_preview');
    }
  };

  // Copy message template to clipboard
  const handleCopyMessage = async () => {
    const message = referidoMessage;
    try {
      await navigator.clipboard.writeText(message);
      setMessageCopied(true);
      setTimeout(() => setMessageCopied(false), 2500);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = message;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setMessageCopied(true);
      setTimeout(() => setMessageCopied(false), 2500);
    }
  };

  // Handle proceeding to signature from phase flow
  const handleProceedToSignature = () => {
    if (referidos.length === 0) {
      showToast('warning', 'Agrega al menos un referido');
      return;
    }
    setStep('signature');
    setShowSignatureModal(true);
  };

  // Handle signature confirmation
  const handleSignatureConfirm = async (signature: string) => {
    if (!user) return;

    setIsSubmitting(true);
    setShowSignatureModal(false);

    try {
      const now = new Date().toISOString();

      // Create referidor
      const referidor: Referidor = {
        id: uuidv4(),
        nombre_completo: referidorForm.nombre_completo,
        telefono: referidorForm.telefono,
        direccion: referidorForm.direccion || undefined,
        regalo_categoria: referidorForm.regalo_categoria,
        regalo_detalle: referidorForm.regalo_detalle || undefined,
        monto_venta: referidorForm.monto_venta ? parseFloat(referidorForm.monto_venta) : undefined,
        fecha_registro: now,
        vendedor_id: user.email,
        vendedor_nombre: user.name,
        firma_digital: signature,
        synced: false,
        createdAt: now,
        updatedAt: now,
      };

      // Create referidos
      const referidosList: Referido[] = referidos.map((r) => ({
        id: uuidv4(),
        referidor_id: referidor.id,
        nombre: r.nombre,
        apellido: r.apellido,
        telefono: r.telefono,
        ciudad: r.ciudad,
        relacion: r.relacion,
        trabaja: r.trabaja,
        mensaje_enviado: r.mensaje_enviado || undefined,
        respuesta_referido: r.respuesta_referido || undefined,
        dato_extra: r.dato_extra || undefined,
        vendedor_id: user.email,
        synced: false,
        createdAt: now,
        updatedAt: now,
      }));

      // Save to IndexedDB
      await indexedDBService.addReferidor(referidor);
      for (const ref of referidosList) {
        await indexedDBService.addReferido(ref);
      }

      // Add to sync queue
      await addToQueue({
        type: 'create_referidor',
        data: { referidor, referidos: referidosList },
      });

      setStep('complete');
      showToast('success', `¡${referidosList.length} referidos guardados!`);
    } catch (error) {
      console.error('Error saving:', error);
      showToast('error', 'Error al guardar. Intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle new capture
  const handleNewCapture = () => {
    setStep('referidor');
    setReferidorForm(initialReferidorState);
    setReferidos([]);
    setErrors({});
    setMessageCopied(false);
  };

  // ========================
  // RENDER: Complete step
  // ========================
  if (step === 'complete') {
    return (
      <div className="max-w-lg mx-auto px-4 py-8">
        <div className="text-center">
          <div className="w-20 h-20 bg-success-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-10 h-10 text-success-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">¡Referidos Guardados!</h2>
          <p className="text-gray-600 mb-6">
            {referidos.length} referido{referidos.length !== 1 ? 's' : ''} de{' '}
            {referidorForm.nombre_completo}
          </p>

          <div className="flex gap-3">
            <Button variant="secondary" onClick={() => navigate('/dashboard')} className="flex-1">
              Ver Dashboard
            </Button>
            <Button variant="primary" onClick={handleNewCapture} className="flex-1">
              Nueva Captura
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-4 py-6">
      {/* ========================
          STEP: Referidor data
          ======================== */}
      {step === 'referidor' && (
        <div className="space-y-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-1">Datos del Referidor</h2>
            <p className="text-sm text-gray-500">
              Persona que proporciona los contactos
            </p>
          </div>

          <div className="space-y-4">
            <Input
              label="Nombre completo"
              value={referidorForm.nombre_completo}
              onChange={(e) =>
                setReferidorForm((prev) => ({ ...prev, nombre_completo: e.target.value }))
              }
              error={errors.nombre_completo}
              required
              placeholder="Ej: María García López"
            />

            <PhoneInput
              value={referidorForm.telefono}
              onChange={(value) => setReferidorForm((prev) => ({ ...prev, telefono: value }))}
              error={errors.telefono}
              required
            />

            <Input
              label="Dirección"
              value={referidorForm.direccion}
              onChange={(e) =>
                setReferidorForm((prev) => ({ ...prev, direccion: e.target.value }))
              }
              placeholder="Ej. 6529 Riverside Ave, Riverside, CA 92506"
            />

            <GiftSelector
              value={referidorForm.regalo_categoria}
              onChange={(value) =>
                setReferidorForm((prev) => ({ ...prev, regalo_categoria: value }))
              }
              error={errors.regalo_categoria}
            />

            <Input
              label="Regalo que ganarás si compran"
              value={referidorForm.regalo_detalle}
              onChange={(e) =>
                setReferidorForm((prev) => ({ ...prev, regalo_detalle: e.target.value.slice(0, 100) }))
              }
              error={errors.regalo_detalle}
              placeholder="Ej: Cat3 - set 5 cuchillos"
              maxLength={100}
            />

            <div>
              <Input
                label="Monto de venta al cliente (opcional)"
                value={referidorForm.monto_venta}
                onChange={(e) =>
                  setReferidorForm((prev) => ({
                    ...prev,
                    monto_venta: e.target.value.replace(/[^0-9.]/g, '').slice(0, 9),
                  }))
                }
                error={errors.monto_venta}
                placeholder="Ej: 1899"
              />
              <p className="text-xs text-gray-500 mt-1">
                Si ya le vendiste al cliente, anota el monto. Si no, déjalo vacío — lo puedes
                agregar después desde el Historial.
              </p>
            </div>
          </div>

          <Button onClick={handleReferidorSubmit} className="w-full">
            Continuar
          </Button>
        </div>
      )}

      {/* ========================
          STEP: Message preview (after referidor, before referidos)
          ======================== */}
      {step === 'message_preview' && (
        <div className="space-y-6">
          <div className="text-center">
            <h2 className="text-xl font-bold text-gray-900 mb-1">Mensaje para el Referidor</h2>
            <p className="text-sm text-gray-500">
              Copia este mensaje y envíalo al referidor desde tu celular
            </p>
          </div>

          {/* Referidor summary — tap to open SMS app */}
          <a
            href={`sms:${referidorForm.telefono}&body=${encodeURIComponent(referidoMessage)}`}
            className="block bg-primary-50 rounded-xl p-4 border border-primary-100 active:bg-primary-100 transition-colors cursor-pointer"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-primary-700 font-medium">
                  {referidorForm.nombre_completo}
                </p>
                <p className="text-xs text-primary-500">
                  {referidorForm.telefono.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')}
                </p>
              </div>
              <div className="flex items-center gap-1 text-xs text-primary-500">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
                <span>Enviar SMS</span>
              </div>
            </div>
          </a>

          {/* Message template */}
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 space-y-3">
            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
              "{referidoMessage}"
            </p>
            <Button
              variant={messageCopied ? 'secondary' : 'primary'}
              onClick={handleCopyMessage}
              className="w-full"
              icon={
                messageCopied ? (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                  </svg>
                )
              }
            >
              {messageCopied ? '¡Mensaje Copiado!' : 'Copiar Mensaje'}
            </Button>
          </div>

          <div className="flex gap-3">
            <Button variant="secondary" onClick={() => setStep('referidor')} className="flex-1">
              Editar Referidor
            </Button>
            <Button onClick={() => setStep('referidos')} className="flex-1">
              Continuar a Nombres
            </Button>
          </div>
        </div>
      )}

      {/* ========================
          STEP: Referidos (4-phase flow)
          ======================== */}
      {step === 'referidos' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-1">
                Referidos de {referidorForm.nombre_completo.split(' ')[0]}
              </h2>
            </div>
            <Button variant="secondary" size="sm" onClick={() => setStep('message_preview')}>
              Volver
            </Button>
          </div>

          <ReferidoPhaseFlow
            referidos={referidos}
            setReferidos={setReferidos}
            onComplete={handleProceedToSignature}
          />
        </div>
      )}

      {/* Signature Modal */}
      <SignatureModal
        isOpen={showSignatureModal}
        onClose={() => setShowSignatureModal(false)}
        onConfirm={handleSignatureConfirm}
      />

      {/* Loading overlay */}
      {isSubmitting && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
            <p className="text-gray-600">Guardando...</p>
          </div>
        </div>
      )}
    </div>
  );
}
